import RotateLeftIcon from '@mui/icons-material/RotateLeft'
import RotateRightIcon from '@mui/icons-material/RotateRight'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from '@mui/material'
// import { ExplanationAccordion } from 'components'
import * as controllers from 'controllers'
import { useEffect, useRef, useState } from 'react'
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material'
import {
  VehicleCheckInAirbagTellTalePhotoDto,
  VehicleCheckInBackupCamPhotoDto,
  VehicleCheckInCheckInClusterPhotoDto,
  VehicleCheckInClusterWithOdometerPhotoDto,
  VehicleCheckInComboPhotoDto,
  VehicleCheckInDriverAirbagPhotoDto,
  VehicleCheckInFrontCornerPhotoDto,
  VehicleCheckInFrontHeadOnPhotoDto,
  VehicleCheckInImporterDoorLabelPhotoDto,
  VehicleCheckInManufacturersLabelPhotoDto,
  VehicleCheckInPassengerAirbagPhotoDto,
  VehicleCheckInRearCornerPhotoDto,
  VehicleCheckInRearHeadOnPhotoDto,
  VehicleCheckInRoofPhotoDto,
  VehicleCheckInTirePressureLabelPhotoDto,
  VehicleCheckInVINOnDashboardPhotoDto
} from 'dtos'

interface VehicleCheckInViewDialogProps {
  photoType:
    | 'manufacturers-label'
    | 'tire-pressure-label'
    | 'importer-door-label'
    | 'combo'
    | 'check-in-cluster'
    | 'cluster-with-odometer'
    | 'driver-airbag'
    | 'backup-cam'
    | 'airbag-tell-tale'
    | 'passenger-airbag'
    | 'vin-on-dashboard'
    | 'roof'
    | 'front-corner'
    | 'front-head-on'
    | 'rear-corner'
    | 'rear-head-on'
  open: boolean
  onClose: () => void
  vehicleGuid: string
  photoTypes: Array<
    | 'manufacturers-label'
    | 'tire-pressure-label'
    | 'importer-door-label'
    | 'combo'
    | 'check-in-cluster'
    | 'cluster-with-odometer'
    | 'driver-airbag'
    | 'backup-cam'
    | 'airbag-tell-tale'
    | 'passenger-airbag'
    | 'vin-on-dashboard'
    | 'roof'
    | 'front-corner'
    | 'front-head-on'
    | 'rear-corner'
    | 'rear-head-on'
  >
}

export default function VehicleCheckView({ photoType, open, onClose, vehicleGuid, photoTypes }: VehicleCheckInViewDialogProps) {
  const [photoSrc, setPhotoSrc] = useState<string>('')
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState<number>(0)

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [rotation, setRotation] = useState<number>(0)
  const [zoom, setZoom] = useState<number>(1)

  const rotateLeft = () => setRotation(prev => prev - 90)
  const rotateRight = () => setRotation(prev => prev + 90)
  const zoomIn = () => setZoom(prev => prev + 0.2)
  const zoomOut = () => setZoom(prev => Math.max(1, prev - 0.2))

  const [isDragging, setIsDragging] = useState(false)
  const [originX, setOriginX] = useState(0)
  const [originY, setOriginY] = useState(0)
  const [translateX, setTranslateX] = useState(0)
  const [translateY, setTranslateY] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  const handleMouseDown = (e: React.MouseEvent<HTMLImageElement>) => {
    if (zoom > 1) {
      e.preventDefault()
      setIsDragging(true)
      setOriginX(e.clientX)
      setOriginY(e.clientY)
    }
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLImageElement>) => {
    if (isDragging) {
      const deltaX = e.clientX - originX
      const deltaY = e.clientY - originY
      let newTranslateX = translateX + deltaX
      let newTranslateY = translateY + deltaY

      if (containerRef.current && imageRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect()

        const imageWidth = imageRef.current.offsetWidth * zoom
        const imageHeight = imageRef.current.offsetHeight * zoom

        const maxTranslateX = (imageWidth - containerRect.width) / 2
        const maxTranslateY = (imageHeight - containerRect.height) / 2

        const padding = 100

        newTranslateX = Math.max(-maxTranslateX - padding, Math.min(newTranslateX, maxTranslateX + padding))
        newTranslateY = Math.max(-maxTranslateY - padding, Math.min(newTranslateY, maxTranslateY + padding))

        setTranslateX(newTranslateX)
        setTranslateY(newTranslateY)
      }

      setTranslateX(newTranslateX)
      setTranslateY(newTranslateY)
      setOriginX(e.clientX)
      setOriginY(e.clientY)
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }
  useEffect(() => {
    if (open) {
      const index = photoTypes.indexOf(photoType)
      setCurrentPhotoIndex(index !== -1 ? index : 0)
    }
  }, [open, photoType, photoTypes])

  useEffect(() => {
    if (open && vehicleGuid) {
      fetchPhoto(photoTypes[currentPhotoIndex])
      console.log('fetchPhoto', currentPhotoIndex)
    } else {
      setPhotoSrc('')
    }
  }, [open, vehicleGuid, currentPhotoIndex, photoTypes])

  const fetchPhoto = async (
    type:
      | 'manufacturers-label'
      | 'tire-pressure-label'
      | 'importer-door-label'
      | 'combo'
      | 'check-in-cluster'
      | 'cluster-with-odometer'
      | 'driver-airbag'
      | 'backup-cam'
      | 'airbag-tell-tale'
      | 'passenger-airbag'
      | 'vin-on-dashboard'
      | 'roof'
      | 'front-corner'
      | 'front-head-on'
      | 'rear-corner'
      | 'rear-head-on'
  ) => {
    setIsLoading(true)
    let documentGuid: string = ''

    try {
      if (type === 'manufacturers-label') {
        const response = await controllers.getVehicleCheckInManufacturersLabelPhotoInformation(vehicleGuid)
        const manufacturersLabelPhoto: VehicleCheckInManufacturersLabelPhotoDto = response.value
        documentGuid = manufacturersLabelPhoto.documentGuid ?? ''

        if (documentGuid) {
          const base64 = await controllers.getVehicleCheckInPhotoBase64(vehicleGuid, documentGuid)
          setPhotoSrc(`data:image/jpeg;base64,${base64}`)
        } else {
          setPhotoSrc('')
        }
      } else if (type === 'tire-pressure-label') {
        const response = await controllers.getVehicleCheckInTirePressureLabelPhotoInformation(vehicleGuid)
        const tirePressureLabelPhoto: VehicleCheckInTirePressureLabelPhotoDto = response.value
        documentGuid = tirePressureLabelPhoto.documentGuid ?? ''

        if (documentGuid) {
          const base64 = await controllers.getVehicleCheckInPhotoBase64(vehicleGuid, documentGuid)
          setPhotoSrc(`data:image/jpeg;base64,${base64}`)
        } else {
          setPhotoSrc('')
        }
      } else if (type === 'importer-door-label') {
        const response = await controllers.getVehicleCheckInImporterDoorLabelPhotoInformation(vehicleGuid)
        const importerDoorLabelPhoto: VehicleCheckInImporterDoorLabelPhotoDto = response.value
        documentGuid = importerDoorLabelPhoto.documentGuid ?? ''

        if (documentGuid) {
          const base64 = await controllers.getVehicleCheckInPhotoBase64(vehicleGuid, documentGuid)
          setPhotoSrc(`data:image/jpeg;base64,${base64}`)
        } else {
          setPhotoSrc('')
        }
      } else if (type === 'combo') {
        const response = await controllers.getVehicleCheckInComboPhotoInformation(vehicleGuid)
        const comboPhoto: VehicleCheckInComboPhotoDto = response.value
        documentGuid = comboPhoto.documentGuid ?? ''

        if (documentGuid) {
          const base64 = await controllers.getVehicleCheckInPhotoBase64(vehicleGuid, documentGuid)
          setPhotoSrc(`data:image/jpeg;base64,${base64}`)
        } else {
          setPhotoSrc('')
        }
      } else if (type === 'check-in-cluster') {
        const response = await controllers.getVehicleCheckInCheckInClusterPhotoInformation(vehicleGuid)
        const checkInClusterPhoto: VehicleCheckInCheckInClusterPhotoDto = response.value
        documentGuid = checkInClusterPhoto.documentGuid ?? ''

        if (documentGuid) {
          const base64 = await controllers.getVehicleCheckInPhotoBase64(vehicleGuid, documentGuid)
          setPhotoSrc(`data:image/jpeg;base64,${base64}`)
        } else {
          setPhotoSrc('')
        }
      } else if (type === 'cluster-with-odometer') {
        const response = await controllers.getVehicleCheckInClusterWithOdometerPhotoInformation(vehicleGuid)
        const clusterWithOdometerPhoto: VehicleCheckInClusterWithOdometerPhotoDto = response.value
        documentGuid = clusterWithOdometerPhoto.documentGuid ?? ''

        if (documentGuid) {
          const base64 = await controllers.getVehicleCheckInPhotoBase64(vehicleGuid, documentGuid)
          setPhotoSrc(`data:image/jpeg;base64,${base64}`)
        } else {
          setPhotoSrc('')
        }
      } else if (type === 'driver-airbag') {
        const response = await controllers.getVehicleCheckInDriverAirbagPhotoInformation(vehicleGuid)
        const driverAirbagPhoto: VehicleCheckInDriverAirbagPhotoDto = response.value
        documentGuid = driverAirbagPhoto.documentGuid ?? ''

        if (documentGuid) {
          const base64 = await controllers.getVehicleCheckInPhotoBase64(vehicleGuid, documentGuid)
          setPhotoSrc(`data:image/jpeg;base64,${base64}`)
        } else {
          setPhotoSrc('')
        }
      } else if (type === 'backup-cam') {
        const response = await controllers.getVehicleCheckInBackupCamPhotoInformation(vehicleGuid)
        const backupCamPhoto: VehicleCheckInBackupCamPhotoDto = response.value
        documentGuid = backupCamPhoto.documentGuid ?? ''

        if (documentGuid) {
          const base64 = await controllers.getVehicleCheckInPhotoBase64(vehicleGuid, documentGuid)
          setPhotoSrc(`data:image/jpeg;base64,${base64}`)
        } else {
          setPhotoSrc('')
        }
      } else if (type === 'airbag-tell-tale') {
        const response = await controllers.getVehicleCheckInAirbagTellTalePhotoInformation(vehicleGuid)
        const airbagTellTalePhoto: VehicleCheckInAirbagTellTalePhotoDto = response.value
        documentGuid = airbagTellTalePhoto.documentGuid ?? ''

        if (documentGuid) {
          const base64 = await controllers.getVehicleCheckInPhotoBase64(vehicleGuid, documentGuid)
          setPhotoSrc(`data:image/jpeg;base64,${base64}`)
        } else {
          setPhotoSrc('')
        }
      } else if (type === 'passenger-airbag') {
        const response = await controllers.getVehicleCheckInPassengerAirbagPhotoInformation(vehicleGuid)
        const passengerAirbagPhoto: VehicleCheckInPassengerAirbagPhotoDto = response.value
        documentGuid = passengerAirbagPhoto.documentGuid ?? ''

        if (documentGuid) {
          const base64 = await controllers.getVehicleCheckInPhotoBase64(vehicleGuid, documentGuid)
          setPhotoSrc(`data:image/jpeg;base64,${base64}`)
        } else {
          setPhotoSrc('')
        }
      } else if (type === 'vin-on-dashboard') {
        const response = await controllers.getVehicleCheckInVINOnDashboardPhotoInformation(vehicleGuid)
        const vinOnDashboardPhoto: VehicleCheckInVINOnDashboardPhotoDto = response.value
        documentGuid = vinOnDashboardPhoto.documentGuid ?? ''

        if (documentGuid) {
          const base64 = await controllers.getVehicleCheckInPhotoBase64(vehicleGuid, documentGuid)
          setPhotoSrc(`data:image/jpeg;base64,${base64}`)
        } else {
          setPhotoSrc('')
        }
      } else if (type === 'roof') {
        const response = await controllers.getVehicleCheckInRoofPhotoInformation(vehicleGuid)
        const roofPhoto: VehicleCheckInRoofPhotoDto = response.value
        documentGuid = roofPhoto.documentGuid ?? ''

        if (documentGuid) {
          const base64 = await controllers.getVehicleCheckInPhotoBase64(vehicleGuid, documentGuid)
          setPhotoSrc(`data:image/jpeg;base64,${base64}`)
        } else {
          setPhotoSrc('')
        }
      } else if (type === 'front-corner') {
        const response = await controllers.getVehicleCheckInFrontCornerPhotoInformation(vehicleGuid)
        const frontCornerPhoto: VehicleCheckInFrontCornerPhotoDto = response.value
        documentGuid = frontCornerPhoto.documentGuid ?? ''

        if (documentGuid) {
          const base64 = await controllers.getVehicleCheckInPhotoBase64(vehicleGuid, documentGuid)
          setPhotoSrc(`data:image/jpeg;base64,${base64}`)
        } else {
          setPhotoSrc('')
        }
      } else if (type === 'front-head-on') {
        const response = await controllers.getVehicleCheckInFrontHeadOnPhotoInformation(vehicleGuid)
        const frontHeadOnPhoto: VehicleCheckInFrontHeadOnPhotoDto = response.value
        documentGuid = frontHeadOnPhoto.documentGuid ?? ''

        if (documentGuid) {
          const base64 = await controllers.getVehicleCheckInPhotoBase64(vehicleGuid, documentGuid)
          setPhotoSrc(`data:image/jpeg;base64,${base64}`)
        } else {
          setPhotoSrc('')
        }
      } else if (type === 'rear-corner') {
        const response = await controllers.getVehicleCheckInRearCornerPhotoInformation(vehicleGuid)
        const rearCornerPhoto: VehicleCheckInRearCornerPhotoDto = response.value
        documentGuid = rearCornerPhoto.documentGuid ?? ''

        if (documentGuid) {
          const base64 = await controllers.getVehicleCheckInPhotoBase64(vehicleGuid, documentGuid)
          setPhotoSrc(`data:image/jpeg;base64,${base64}`)
        } else {
          setPhotoSrc('')
        }
      } else if (type === 'rear-head-on') {
        const response = await controllers.getVehicleCheckInRearHeadOnPhotoInformation(vehicleGuid)
        const rearHeadOnPhoto: VehicleCheckInRearHeadOnPhotoDto = response.value
        documentGuid = rearHeadOnPhoto.documentGuid ?? ''

        if (documentGuid) {
          const base64 = await controllers.getVehicleCheckInPhotoBase64(vehicleGuid, documentGuid)
          setPhotoSrc(`data:image/jpeg;base64,${base64}`)
        } else {
          setPhotoSrc('')
        }
      }
    } catch (error) {
      console.error('Error fetching photo:', error)
      console.log('fetchPhoto', type)
      setPhotoSrc('')
    } finally {
      setIsLoading(false)
    }
  }

  const goToPreviousPhoto = () => {
    setCurrentPhotoIndex(prevIndex => (prevIndex > 0 ? prevIndex - 1 : photoTypes.length - 1))
    console.log(currentPhotoIndex)
  }

  const goToNextPhoto = () => {
    setCurrentPhotoIndex(prevIndex => {
      return prevIndex < photoTypes.length - 1 ? prevIndex + 1 : 0
    })
    console.log(currentPhotoIndex)
  }

  useEffect(() => {
    setRotation(0)
    setZoom(1)
    setTranslateX(0)
    setTranslateY(0)
  }, [photoSrc])

  const getTitle = () => {
    switch (photoTypes[currentPhotoIndex]) {
      case 'manufacturers-label':
        return 'Manufacturer Label Photo'
      case 'tire-pressure-label':
        return 'Tire Pressure Label Photo'
      case 'importer-door-label':
        return 'Importer Door Label Photo'
      case 'combo':
        return 'Combo Photo'
      case 'check-in-cluster':
        return 'Speedometer Before Modification Photo'
      case 'cluster-with-odometer':
        return 'Odometer Before Modification Photo'
      case 'driver-airbag':
        return 'Driver Airbag Photo'
      case 'backup-cam':
        return 'Backup Cam Photo'
      case 'airbag-tell-tale':
        return 'Airbag Tell-Tale Photo'
      case 'passenger-airbag':
        return 'Passenger Airbag Photo'
      case 'vin-on-dashboard':
        return 'VIN On Dashboard Photo'
      case 'roof':
        return 'Roof Photo'
      case 'front-corner':
        return 'Front Corner Photo'
      case 'front-head-on':
        return 'Front Head On Photo'
      case 'rear-corner':
        return 'Rear Corner Photo'
      case 'rear-head-on':
        return 'Rear Head On Photo'
      default:
        return 'View Check In Photo'
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogContent>
        <Grid container spacing={2} alignItems='center'>
          <DialogTitle>{getTitle()}</DialogTitle>

          <Grid item xs={12}>
            {isLoading ? (
              <Grid container justifyContent='center'>
                <CircularProgress />
              </Grid>
            ) : photoSrc ? (
              <div
                ref={containerRef}
                style={{
                  overflow: 'hidden',
                  width: '100%',
                  height: '60vh',
                  backgroundColor: 'black',
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '8px',
                  borderRadius: '8px'
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <Button
                  style={{ position: 'absolute', top: '50%', left: '10px', transform: 'translateY(-50%)', zIndex: 10 }}
                  onClick={goToPreviousPhoto}
                  variant='text'
                >
                  <KeyboardArrowLeft style={{ fontSize: 40 }} />
                </Button>
                <Button
                  style={{ position: 'absolute', top: '50%', right: '10px', transform: 'translateY(-50%)', zIndex: 10 }}
                  onClick={goToNextPhoto}
                  variant='text'
                >
                  <KeyboardArrowRight style={{ fontSize: 40 }} />
                </Button>
                <img
                  ref={imageRef}
                  src={photoSrc}
                  alt={getTitle()}
                  style={{
                    width: '80%',
                    height: '100%',
                    transform: `translate(${translateX}px, ${translateY}px) rotate(${rotation}deg) scale(${zoom})`,
                    transformOrigin: 'center center',
                    cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                    transition: isDragging ? 'none' : 'transform 0.3s ease'
                  }}
                  draggable={false}
                />
              </div>
            ) : (
              <Grid container justifyContent='center'>
                <Typography variant='body1'>No Photo Available</Typography>
              </Grid>
            )}

            <Grid container justifyContent='space-between' alignItems='center'>
              <Grid item ml={1.25}>
                <Button endIcon={<ZoomInIcon />} color='primary' onClick={zoomIn} variant='text'>
                  Zoom In
                </Button>
              </Grid>

              <Grid item mr={1.25}>
                <Button endIcon={<ZoomOutIcon />} color='primary' onClick={zoomOut} variant='text'>
                  Zoom Out
                </Button>
              </Grid>
            </Grid>
            <Grid container xs={12} margin='auto' mt={1} justifyContent='center' alignItems='center'>
              <Grid item xs={4} sm='auto'>
                <Button startIcon={<RotateLeftIcon />} color='primary' onClick={rotateLeft} variant='text'>
                  ROTATE LEFT
                </Button>
              </Grid>

              <Grid item xs={4}></Grid>

              <Grid item xs={4} sm='auto'>
                <Button endIcon={<RotateRightIcon />} color='primary' onClick={rotateRight} variant='text'>
                  ROTATE RIGHT
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button color='inherit' onClick={onClose} variant='text'>
          CLOSE
        </Button>
      </DialogActions>
    </Dialog>
  )
}
